import { useEffect, useState } from 'react';
import { useT } from '../context/LocaleContext';
import { displayName } from '../lib/format';
import { supabase } from '../lib/supabase';

async function fetchJobCardForBooking(bookingId) {
  const { data, error } = await supabase
    .from('job_cards')
    .select('id, assigned_technician_id, status')
    .eq('booking_id', bookingId)
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export default function TechnicianAssignSelect({
  bookingId,
  jobCardId,
  assignedTechnicianId,
  branchId,
  onAssigned,
}) {
  const t = useT();
  const [technicians, setTechnicians] = useState([]);
  const [value, setValue] = useState(assignedTechnicianId || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setValue(assignedTechnicianId || '');
  }, [assignedTechnicianId]);

  useEffect(() => {
    let alive = true;
    async function load() {
      let query = supabase
        .from('profiles')
        .select('id, first_name, last_name, full_name, branch_id')
        .eq('role', 'technician')
        .order('full_name');
      if (branchId) query = query.eq('branch_id', branchId);
      const { data, error: loadError } = await query;
      if (!alive) return;
      if (loadError) {
        setError(loadError.message);
        return;
      }
      setTechnicians(data || []);
    }
    load();
    return () => {
      alive = false;
    };
  }, [branchId]);

  async function ensureJobCard() {
    if (jobCardId) return jobCardId;
    const { data, error: rpcError } = await supabase.rpc(
      'ensure_job_card_for_booking_id',
      { p_booking_id: bookingId }
    );
    if (rpcError) throw rpcError;
    return data;
  }

  async function assign(nextId) {
    const previous = value;
    setBusy(true);
    setError('');
    setSaved(false);
    try {
      const cardId = await ensureJobCard();
      const technicianId = nextId || null;
      const { data, error: rpcError } = await supabase.rpc('assign_job_card_technician', {
        p_job_card_id: cardId,
        p_technician_id: technicianId,
      });
      if (rpcError) throw rpcError;

      const savedCard = data || (await fetchJobCardForBooking(bookingId));
      const assignedId = savedCard?.assigned_technician_id || technicianId || '';

      setValue(assignedId);
      onAssigned?.(assignedId || null, savedCard?.id || cardId);
      setSaved(true);
    } catch (assignError) {
      setValue(previous);
      setError(assignError?.message || t('assign.failed'));
    } finally {
      setBusy(false);
    }
  }

  if (!technicians.length) {
    return (
      <p className="lede" style={{ margin: 0 }}>
        {t('assign.empty')}
      </p>
    );
  }

  return (
    <div className="assign-tech">
      <label>
        {t('assign.label')}
        <select
          value={value}
          disabled={busy}
          onChange={(event) => assign(event.target.value)}
        >
          <option value="">{t('assign.unassigned')}</option>
          {technicians.map((tech) => (
            <option key={tech.id} value={tech.id}>
              {displayName(tech)}
            </option>
          ))}
        </select>
      </label>
      {saved ? <p className="lede">{t('assign.saved')}</p> : null}
      {error ? <p className="form-error">{error}</p> : null}
    </div>
  );
}
